/**
 * VisualDebugPanel — Internes Debug-Panel für visuelles Markenprofil.
 *
 * Zeigt den vollständigen VisualDebugSnapshot aus /api/visual-debug/snapshot:
 *   1. Visuelles Markenprofil (Rohdaten)
 *   2. brand_visual_hints (Farbkacheln + dict)
 *   3. image_prompt_hint je Pipeline (Tabelle + Copy-Button)
 *   4. ImageGenerationBrief je Pipeline (Accordion + Copy-Button)
 *
 * Sichtbarkeits-Guard:
 *   SHOW_VISUAL_DEBUG = true → immer sichtbar (für interne Dev/Design-Arbeit).
 *   Zum Ausblenden für Endnutzer: auf false setzen oder an process.env knüpfen.
 *
 * Dieses Panel ist bewusst als interne Hilfsfläche gestaltet:
 *   - Grauer Rahmen, "INTERN"-Badge
 *   - Kein Platz auf produktiven User-Seiten
 *   - Nur in SettingsPage am Ende eingebunden
 */

import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Sichtbarkeits-Guard ────────────────────────────────────────────────────────
// false → Panel komplett ausgeblendet (für Produktion)
// true  → Panel sichtbar (für interne Entwicklung/Design)
const SHOW_VISUAL_DEBUG = true;

// ── Hilfsfunktion: Text in Zwischenablage kopieren ────────────────────────────
async function copyToClipboard(text, onSuccess) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Legacy-Fallback
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    onSuccess?.();
  } catch {
    /* silent fail */
  }
}

// ── CopyButton ────────────────────────────────────────────────────────────────
const CopyButton = ({ text, label = "Kopieren" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    await copyToClipboard(text, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      disabled={!text}
      className={`text-xs px-2 py-1 rounded transition-colors flex-shrink-0 ${
        copied
          ? "bg-green-100 text-green-700"
          : "bg-stone-100 text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
      }`}
    >
      {copied ? "✓ Kopiert" : label}
    </button>
  );
};

// ── ColorChip ─────────────────────────────────────────────────────────────────
const DebugColorChip = ({ color, label }) => {
  if (!color) return null;
  return (
    <div className="flex items-center gap-1.5" title={`${label}: ${color}`}>
      <div
        className="w-5 h-5 rounded border border-stone-300 flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-xs font-mono text-stone-600">{label}</span>
      <span className="text-xs font-mono text-stone-400">{color}</span>
    </div>
  );
};

// ── Sektion 1: Visuelles Markenprofil ─────────────────────────────────────────
const VisualProfileSection = ({ profile }) => {
  if (!profile) {
    return (
      <p className="text-xs text-stone-400 italic">
        Kein visuelles Markenprofil in der Datenbank. Bitte Website-Analyse durchführen.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-4">
        <DebugColorChip color={profile.primary_color}    label="Primär"       />
        <DebugColorChip color={profile.secondary_color}  label="Sekundär"     />
        <DebugColorChip color={profile.background_color} label="Hintergrund"  />
      </div>
      {profile.logo_url && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-500">Logo:</span>
          <img
            src={profile.logo_url}
            alt="Logo"
            className="h-6 max-w-[120px] object-contain border border-stone-200 rounded px-1"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <span className="text-xs font-mono text-stone-400 truncate max-w-[200px]">{profile.logo_url}</span>
        </div>
      )}
      {profile.notes && (
        <p className="text-xs text-stone-400 italic">{profile.notes}</p>
      )}
      {/* Raw JSON scrollbar */}
      <details className="mt-2">
        <summary className="text-xs text-stone-400 cursor-pointer hover:text-stone-600">
          Rohdaten (JSON)
        </summary>
        <pre className="mt-1 text-xs bg-stone-50 border border-stone-200 rounded p-2 overflow-auto max-h-40 text-stone-600">
          {JSON.stringify(profile, null, 2)}
        </pre>
      </details>
    </div>
  );
};

// ── Sektion 2: brand_visual_hints ─────────────────────────────────────────────
const BrandVisualHintsSection = ({ hints }) => {
  if (!hints) {
    return (
      <p className="text-xs text-stone-400 italic">
        Keine brand_visual_hints verfügbar (kein Farbprofil erkannt).
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-4">
        <DebugColorChip color={hints.primary_color}    label="primary_color"    />
        <DebugColorChip color={hints.secondary_color}  label="secondary_color"  />
        <DebugColorChip color={hints.background_color} label="background_color" />
      </div>
      {hints.color_hint && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-500">color_hint:</span>
          <span className="text-xs font-mono text-stone-600">{hints.color_hint}</span>
        </div>
      )}
      <details className="mt-1">
        <summary className="text-xs text-stone-400 cursor-pointer hover:text-stone-600">
          Rohdaten (JSON)
        </summary>
        <pre className="mt-1 text-xs bg-stone-50 border border-stone-200 rounded p-2 overflow-auto max-h-32 text-stone-600">
          {JSON.stringify(hints, null, 2)}
        </pre>
      </details>
    </div>
  );
};

// ── Sektion 3: image_prompt_hint Tabelle ─────────────────────────────────────
const HintExamplesTable = ({ examples }) => {
  const pipelines = ["simple_flow", "masterpost", "content_tools", "strategy"];
  const labels = {
    simple_flow:   "Simple Flow",
    masterpost:    "Masterpost",
    content_tools: "Content Tools",
    strategy:      "Strategy",
  };
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-stone-200">
            <th className="text-left py-1.5 pr-4 text-stone-500 font-medium w-32">Pipeline</th>
            <th className="text-left py-1.5 pr-2 text-stone-500 font-medium">Hint</th>
            <th className="py-1.5 text-stone-500 font-medium w-24 text-right">Kopieren</th>
          </tr>
        </thead>
        <tbody>
          {pipelines.map((key) => {
            const hint = examples?.[key];
            return (
              <tr key={key} className="border-b border-stone-100">
                <td className="py-2 pr-4 font-medium text-stone-600">{labels[key]}</td>
                <td className="py-2 pr-2 text-stone-500 font-mono leading-relaxed">
                  {hint ?? <span className="italic text-stone-300">—</span>}
                </td>
                <td className="py-2 text-right">
                  <CopyButton text={hint} label="Copy" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Brief → menschenlesbarer Prompt-Text ────────────────────────────────────
function briefToPromptText(brief) {
  if (!brief) return "";
  const parts = [];
  if (brief.format && brief.channel) {
    parts.push(`Erstelle ein ${brief.format}-Bild für ${brief.channel}.`);
  }
  if (brief.target_audience) parts.push(`Zielgruppe: ${brief.target_audience}.`);
  if (brief.core_message)    parts.push(`Kernbotschaft: ${brief.core_message}`);
  const colors = [brief.primary_color, brief.secondary_color].filter(Boolean);
  if (colors.length) parts.push(`Farben: ${colors.join(" / ")}.`);
  if (brief.style_keywords?.length) {
    parts.push(`Stil: ${brief.style_keywords.join(", ")}.`);
  }
  if (brief.image_prompt_hint) parts.push(brief.image_prompt_hint);
  if (brief.notes)             parts.push(`Hinweis: ${brief.notes}`);
  return parts.join(" ");
}

// ── Sektion 4: ImageGenerationBrief Accordion ────────────────────────────────
const BriefExamplesSection = ({ examples }) => {
  const [open, setOpen] = useState(null);
  const pipelines = ["simple_flow", "masterpost", "content_tools", "strategy"];
  const labels = {
    simple_flow:   "Simple Flow",
    masterpost:    "Masterpost",
    content_tools: "Content Tools",
    strategy:      "Strategy",
  };

  return (
    <div className="space-y-1">
      {pipelines.map((key) => {
        const brief = examples?.[key];
        const isOpen = open === key;
        const promptText = briefToPromptText(brief);
        return (
          <div key={key} className="border border-stone-200 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              onClick={() => setOpen(isOpen ? null : key)}
            >
              <span>{labels[key]}</span>
              <span className="flex items-center gap-2">
                {brief?.channel && (
                  <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-500">
                    {brief.channel}/{brief.format}
                  </span>
                )}
                <span>{isOpen ? "▲" : "▼"}</span>
              </span>
            </button>

            {isOpen && (
              <div className="px-3 pb-3 pt-1 bg-stone-50 border-t border-stone-200 space-y-2">
                {brief ? (
                  <>
                    {/* Kernfelder */}
                    <div className="space-y-1.5">
                      {brief.core_message && (
                        <div>
                          <span className="text-xs text-stone-400 font-medium">Kernbotschaft</span>
                          <p className="text-xs text-stone-600 mt-0.5">{brief.core_message}</p>
                        </div>
                      )}
                      {brief.style_keywords?.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-stone-400 font-medium">Stil:</span>
                          {brief.style_keywords.map((kw) => (
                            <span key={kw} className="text-xs px-1.5 py-0.5 bg-white border border-stone-200 rounded text-stone-600">
                              {kw}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <DebugColorChip color={brief.primary_color}    label="Primär"      />
                        <DebugColorChip color={brief.secondary_color}  label="Sekundär"    />
                        <DebugColorChip color={brief.background_color} label="Hintergrund" />
                      </div>
                      {brief.notes && (
                        <p className="text-xs text-stone-400 italic">{brief.notes}</p>
                      )}
                    </div>

                    {/* Copy-Buttons */}
                    <div className="flex gap-2 pt-1">
                      <CopyButton text={promptText} label="Als Text-Prompt kopieren" />
                      <CopyButton text={JSON.stringify(brief, null, 2)} label="Als JSON kopieren" />
                    </div>

                    {/* Raw JSON */}
                    <details>
                      <summary className="text-xs text-stone-400 cursor-pointer hover:text-stone-600">
                        Rohdaten (JSON)
                      </summary>
                      <pre className="mt-1 text-xs bg-white border border-stone-200 rounded p-2 overflow-auto max-h-48 text-stone-600">
                        {JSON.stringify(brief, null, 2)}
                      </pre>
                    </details>
                  </>
                ) : (
                  <p className="text-xs text-stone-400 italic">Keine Daten für diese Pipeline.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ── JSON-Export-Hilfsfunktion ─────────────────────────────────────────────────
function downloadJsonSnapshot(snapshot) {
  if (!snapshot) return;
  try {
    const now = new Date();
    const ts  = now.toISOString().replace(/[-:T]/g, "").slice(0, 15); // YYYYMMDD-HHMMSS
    const filename = `visual-debug-snapshot-${ts}.json`;
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch { /* silent fail */ }
}

// ── SectionToggle — kleine Toggle-Checkbox ───────────────────────────────────
const SectionToggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-1.5 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-3 h-3 accent-stone-600"
    />
    <span className="text-xs text-stone-500">{label}</span>
  </label>
);

// ── Hauptkomponente ───────────────────────────────────────────────────────────
const VisualDebugPanel = () => {
  const [snapshot, setSnapshot]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [exported, setExported]   = useState(false);

  // ── Section-Toggles (C1) ──
  const [showProfile, setShowProfile]         = useState(true);
  const [showHints,   setShowHints]           = useState(true);
  const [showHintTable, setShowHintTable]     = useState(true);
  const [showBriefs,  setShowBriefs]          = useState(true);

  useEffect(() => {
    if (collapsed) return;   // nur laden wenn aufgeklappt
    setLoading(true);
    axios
      .get(`${API}/visual-debug/snapshot`)
      .then((res) => {
        setSnapshot(res.data || null);
        setError(null);
      })
      .catch(() => {
        setError("Debug-Daten konnten nicht geladen werden.");
        setSnapshot(null);
      })
      .finally(() => setLoading(false));
  }, [collapsed]);  // re-fetch jedes Mal, wenn Panel geöffnet wird

  // Sichtbarkeits-Guard — nach allen Hooks, damit Rules of Hooks eingehalten werden
  if (!SHOW_VISUAL_DEBUG) return null;

  const handleExport = () => {
    downloadJsonSnapshot(snapshot);
    setExported(true);
    setTimeout(() => setExported(false), 2000);
  };

  return (
    <div className="border-2 border-dashed border-stone-300 rounded-xl overflow-hidden">
      {/* Header — immer sichtbar, klickbar zum Ausklappen */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors text-left"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">🔬</span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-stone-700 text-sm">Visuelles Debug-Panel</span>
              <span className="text-xs px-1.5 py-0.5 bg-stone-200 text-stone-600 rounded font-mono">
                INTERN
              </span>
            </div>
            <p className="text-xs text-stone-400 mt-0.5">
              Nur für interne Tests &amp; Design-Abstimmung sichtbar
            </p>
          </div>
        </div>
        <span className="text-stone-400 text-sm">{collapsed ? "▼ Öffnen" : "▲ Schließen"}</span>
      </button>

      {/* Panel-Inhalt */}
      {!collapsed && (
        <div className="border-t border-stone-200 p-5 space-y-6 bg-white">

          {/* Toolbar: Section-Toggles + Export-Button */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-stone-100">
            {/* C1: Section-Toggles */}
            <div className="flex flex-wrap gap-4">
              <SectionToggle label="Markenprofil"           checked={showProfile}   onChange={setShowProfile}   />
              <SectionToggle label="brand_visual_hints"     checked={showHints}     onChange={setShowHints}     />
              <SectionToggle label="image_prompt_hint"      checked={showHintTable} onChange={setShowHintTable} />
              <SectionToggle label="ImageGenerationBrief"   checked={showBriefs}    onChange={setShowBriefs}    />
            </div>
            {/* C2: JSON-Export */}
            <button
              onClick={handleExport}
              disabled={!snapshot}
              className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                exported
                  ? "bg-green-100 text-green-700"
                  : snapshot
                    ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    : "bg-stone-50 text-stone-300 cursor-not-allowed"
              }`}
              title={snapshot ? "Snapshot als JSON-Datei herunterladen" : "Noch keine Daten geladen"}
            >
              <span>{exported ? "✓" : "⬇"}</span>
              <span>{exported ? "Heruntergeladen" : "Snapshot als JSON"}</span>
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8 text-stone-400 text-sm gap-2">
              <span className="animate-spin inline-block w-4 h-4 border-2 border-stone-300 border-t-stone-600 rounded-full" />
              Lade Debug-Daten...
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && snapshot && (
            <>
              {/* 1. Visuelles Markenprofil */}
              {showProfile && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    1 · Visuelles Markenprofil (Rohdaten)
                  </h3>
                  <VisualProfileSection profile={snapshot.visual_brand_profile} />
                </section>
              )}

              {showProfile && showHints && <hr className="border-stone-100" />}

              {/* 2. brand_visual_hints */}
              {showHints && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    2 · brand_visual_hints
                  </h3>
                  <BrandVisualHintsSection hints={snapshot.brand_visual_hints} />
                </section>
              )}

              {(showHints || showProfile) && showHintTable && <hr className="border-stone-100" />}

              {/* 3. image_prompt_hint je Pipeline */}
              {showHintTable && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    3 · image_prompt_hint je Pipeline
                  </h3>
                  <HintExamplesTable examples={snapshot.image_prompt_hint_examples} />
                </section>
              )}

              {(showHints || showProfile || showHintTable) && showBriefs && <hr className="border-stone-100" />}

              {/* 4. ImageGenerationBrief je Pipeline */}
              {showBriefs && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    4 · ImageGenerationBrief je Pipeline
                  </h3>
                  <BriefExamplesSection examples={snapshot.image_generation_brief_examples} />
                </section>
              )}
            </>
          )}

          {!loading && !error && !snapshot && (
            <p className="text-xs text-stone-400 italic text-center py-4">
              Keine Debug-Daten verfügbar.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VisualDebugPanel;
